

import React, { useState, useMemo } from 'react';
import { Expense, Category, User } from '../types';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, Tooltip as RechartsTooltip, XAxis } from 'recharts';
import { Sparkles, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownLeft, MoreHorizontal, Bell } from 'lucide-react';
import { getFinancialAdvice } from '../services/geminiService';

interface DashboardProps {
  expenses: Expense[]; // Should be pre-filtered/projected monthly expenses
  categories: Category[];
  currentUser: User;
  selectedDate: Date;
  onViewNotifications: () => void;
}

// Modern vibrant palette for charts
const CHART_COLORS = ['#cbf85d', '#18181b', '#94a3b8', '#e4e4e7', '#fecca6'];

export const Dashboard: React.FC<DashboardProps> = ({ expenses, categories, currentUser, selectedDate, onViewNotifications }) => {
  const [aiAdvice, setAiAdvice] = useState<string | null>(null);
  const [loadingAi, setLoadingAi] = useState(false);

  // Use props directly (App.tsx handles filtering/projection)
  const monthlyExpenses = expenses;

  // Calculations
  const totalExpense = useMemo(() => 
    monthlyExpenses.filter(e => e.type === 'expense').reduce((acc, cur) => acc + cur.amount, 0), 
  [monthlyExpenses]);

  const totalIncome = useMemo(() => 
    monthlyExpenses.filter(e => e.type === 'income').reduce((acc, cur) => acc + cur.amount, 0), 
  [monthlyExpenses]);

  const balance = totalIncome - totalExpense;
  
  // Data for Charts (Only Expenses)
  const categoryData = useMemo(() => {
    const data: Record<string, number> = {};
    monthlyExpenses.filter(e => e.type === 'expense').forEach(e => {
      const cat = categories.find(c => c.id === e.categoryId);
      const name = cat ? cat.name : 'Outros';
      data[name] = (data[name] || 0) + e.amount;
    });
    return Object.keys(data).map(key => ({ name: key, value: data[key] }));
  }, [monthlyExpenses, categories]);

  const dailyData = useMemo(() => {
    const daysMap: Record<number, number> = {};
    const daysInMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0).getDate();
    
    monthlyExpenses.filter(e => e.type === 'expense').forEach(e => {
        const day = new Date(e.date).getDate();
        daysMap[day] = (daysMap[day] || 0) + e.amount;
    });

    const result = [];
    for(let w=1; w<=4; w++) {
        let weekTotal = 0;
        const startDay = (w-1)*7 + 1;
        const endDay = Math.min(w*7, daysInMonth);
        
        for(let d=startDay; d<=endDay; d++) {
            weekTotal += (daysMap[d] || 0);
        }
        result.push({
            day: `Sem ${w}`,
            fullDate: `Semana ${w}`,
            amount: weekTotal
        });
    }
    return result;
  }, [monthlyExpenses, selectedDate]);

  const handleAskAI = async () => {
    setLoadingAi(true);
    const advice = await getFinancialAdvice(monthlyExpenses, categories);
    setAiAdvice(advice);
    setLoadingAi(false);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-zinc-800 overflow-hidden border border-slate-300 dark:border-zinc-700">
             <img src={currentUser.avatarUrl || "https://picsum.photos/100/100"} alt="Avatar" className="w-full h-full object-cover" />
          </div>
          <div>
             <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Olá,</p>
             <h2 className="text-lg font-bold text-slate-800 dark:text-white leading-none">{currentUser.name}</h2>
          </div>
        </div>
        <button 
          onClick={onViewNotifications}
          className="p-2.5 bg-white dark:bg-zinc-900 rounded-full border border-slate-100 dark:border-zinc-800 shadow-sm relative text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-zinc-800 transition-colors active:scale-95"
        >
          <Bell size={20} />
          <span className="absolute top-2 right-2.5 w-2 h-2 bg-red-500 rounded-full border border-white dark:border-zinc-900"></span>
        </button>
      </div>

      {/* Main Card (Dark Theme) */}
      <div className="relative overflow-hidden bg-brand-dark text-white rounded-[2rem] p-8 shadow-2xl">
        {/* Background blobs */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-brand-lime opacity-10 blur-[80px] rounded-full translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-500 opacity-10 blur-[60px] rounded-full -translate-x-1/3 translate-y-1/3"></div>

        <div className="relative z-10">
          <div className="flex justify-between items-start mb-8">
            <div>
              <p className="text-slate-400 text-sm font-medium mb-1 capitalize">
                 Saldo em {selectedDate.toLocaleString('pt-BR', { month: 'long' })}
              </p>
              <h3 className="text-4xl font-bold tracking-tight">
                <span className="text-brand-lime">R$</span> {balance.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </h3>
            </div>
            <div className="p-2 bg-white/10 backdrop-blur-md rounded-xl">
               <TrendingUp className="text-brand-lime" size={24} />
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-1 bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/5">
              <div className="flex items-center gap-2 text-slate-300 mb-1 text-xs">
                <div className="w-5 h-5 rounded-full bg-brand-lime/20 flex items-center justify-center text-brand-lime">
                   <ArrowDownLeft size={12} />
                </div>
                Entradas
              </div>
              <p className="text-lg font-semibold">R$ {totalIncome.toFixed(0)}</p>
            </div>
            <div className="flex-1 bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/5">
              <div className="flex items-center gap-2 text-slate-300 mb-1 text-xs">
                <div className="w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center text-red-400">
                   <ArrowUpRight size={12} />
                </div>
                Saídas
              </div>
              <p className="text-lg font-semibold">R$ {totalExpense.toFixed(0)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* AI Button */}
      {monthlyExpenses.length > 0 && !aiAdvice && (
        <button 
          onClick={handleAskAI}
          disabled={loadingAi}
          className="w-full bg-gradient-to-r from-brand-lime to-green-400 text-brand-dark p-1 rounded-2xl shadow-lg shadow-brand-lime/20 transition-all hover:scale-[1.01] active:scale-[0.99]"
        >
          <div className="bg-white/40 backdrop-blur-sm rounded-xl py-3 px-4 flex items-center justify-center gap-2">
             <Sparkles size={18} className="text-brand-dark" />
             <span className="font-bold text-sm">{loadingAi ? 'Analisando finanças...' : 'Pedir Análise da IA'}</span>
          </div>
        </button>
      )}

      {/* AI Insight Result */}
      {aiAdvice && (
        <div className="bg-brand-black p-6 rounded-[1.5rem] shadow-soft animate-in slide-in-from-bottom-4">
          <div className="flex items-center gap-2 mb-3 text-brand-lime">
            <Sparkles size={20} className="fill-brand-lime" />
            <h3 className="font-bold tracking-wide text-sm uppercase">Fiantro AI</h3>
          </div>
          <div className="prose prose-sm prose-invert max-w-none text-slate-300 leading-relaxed">
            <div dangerouslySetInnerHTML={{ __html: aiAdvice.replace(/\n/g, '<br/>').replace(/\*\*(.*?)\*\*/g, '<b class="text-white">$1</b>') }} />
          </div>
        </div>
      )}

      {/* Charts Section */}
      <div className="grid grid-cols-1 gap-6">
        
        {/* Spending Structure */}
        <div className="bg-white dark:bg-zinc-900 p-6 rounded-[2rem] shadow-soft">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-bold text-slate-800 dark:text-white text-lg">Por Categoria</h3>
            <button className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"><MoreHorizontal size={20} /></button>
          </div>
          
          {categoryData.length > 0 ? (
            <div className="flex items-center gap-6">
                <div className="w-1/2 h-40 relative min-w-[150px]">
                <ResponsiveContainer width="100%" height="100%" minWidth={150}>
                    <PieChart>
                    <Pie
                        data={categoryData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        paddingAngle={6}
                        dataKey="value"
                        cornerRadius={6}
                        stroke="none"
                    >
                        {categoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                    </Pie>
                    </PieChart>
                </ResponsiveContainer>
                {/* Center Text */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="text-xs font-bold text-slate-400">Total</span>
                </div>
                </div>

                <div className="w-1/2 space-y-3">
                {categoryData.slice(0, 3).map((entry, index) => (
                    <div key={index} className="flex justify-between items-center text-sm">
                        <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}></div>
                        <span className="text-slate-600 dark:text-slate-300 truncate max-w-[80px]">{entry.name}</span>
                        </div>
                        <span className="font-bold text-slate-800 dark:text-white">{((entry.value / totalExpense) * 100).toFixed(0)}%</span>
                    </div>
                ))}
                </div>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400 text-sm">Sem despesas neste mês</div>
          )}
        </div>

        {/* Weekly Activity */}
        <div className="bg-white dark:bg-zinc-900 p-6 rounded-[2rem] shadow-soft">
           <h3 className="font-bold text-slate-800 dark:text-white text-lg mb-6">Atividade Semanal</h3>
           {monthlyExpenses.some(e => e.type === 'expense') ? (
            <div className="h-48 w-full min-w-[150px]">
                <ResponsiveContainer width="100%" height="100%" minWidth={150}>
                <BarChart data={dailyData}>
                    <XAxis 
                    dataKey="day" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{fontSize: 12, fill: '#94a3b8'}} 
                    dy={10}
                    />
                    <RechartsTooltip 
                    cursor={{fill: '#f4f4f5', radius: 8}} 
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} 
                    />
                    <Bar dataKey="amount" fill="#18181b" radius={[6, 6, 6, 6]} barSize={32}>
                        {dailyData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={index % 2 === 0 ? '#18181b' : '#cbf85d'} />
                        ))}
                    </Bar>
                </BarChart>
                </ResponsiveContainer>
            </div>
           ) : (
             <div className="text-center py-8 text-slate-400 text-sm">Nenhuma saída registrada</div>
           )}
        </div>

      </div>
    </div>
  );
};